import { SvgIconComponent, ViewList } from '@mui/icons-material';
import { AppBar, Box, Button, Grid, ListItem, ListItemButton, ListItemText, Paper, Toolbar, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChildComponentProps, FixedSizeList } from 'react-window';
import { GeneralIcons } from '../../settings/icons';
import { vsprintf } from 'sprintf-js';

interface IProps 
{
    typeName: string;
    typeNamePlural: string;
    items: IItem[];
    onItemClick: (index: number)=>void;
    actions?: {
        title: string;
        icon?: SvgIconComponent;
        onAction: ()=>void;
    }[];
}

export interface IItem
{
    title: string;
    icon?: SvgIconComponent;
    typeTitle?: string;
}

const SelectableItemList: FC<IProps> = ({ typeName, typeNamePlural, items, onItemClick, actions }) => 
{
    const { t } = useTranslation();
    const renderRow = (props: ListChildComponentProps) => {
        const { index, style } = props;
        let item = items[index];
        const Icon = item.icon ?? GeneralIcons.Default;
        const title = item.title;
        const typeTitle = item.typeTitle ?? t('unknown');
      
        return (<>
            <ListItem key={`selectable-list-item-${item.title}-${index}`} style={style} component="div" disablePadding>
                <ListItemButton onClick={()=>{onItemClick(index)}}>
                    <Icon sx={{marginRight: "20px"}} />
                    <ListItemText primary={title} />
                    <Typography sx={{fontSize: "10px", marginRight: "5px"}}>{`${typeName} ${t('general.type')}:`}</Typography>
                    <Typography sx={{fontSize: "12px"}}>{typeTitle}</Typography>
                </ListItemButton>
            </ListItem>
        </>);
    }

    const listCount = items.length;
    const listHeight = (listCount*47 > 800 ? 800 : listCount*46) + 5;
    return (<>
        <Paper sx={{ maxWidth: 936, margin: 'auto', height: 'auto', overflow: 'hidden' }}>
            <AppBar
                position="static"
                color="default"
                elevation={0}
                sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}
            >
                <Toolbar>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <ViewList color="inherit" sx={{ display: 'block' }} />
                        </Grid>
                        <Grid item xs>
                            <Typography>
                                {typeNamePlural}
                            </Typography>
                        </Grid>
                        <Grid item>
                            {
                                actions ? actions.map(action=>{
                                    const icon = action.icon ? <action.icon sx={{ml: "-8px", mr: "2px", p: "3px"}} /> : <></>;
                                    return (<Button 
                                        key={`selectable-list-item-action-btn-${action.title}`} 
                                        variant="contained" 
                                        sx={{ mr: 1 }} 
                                        onClick={()=>action.onAction()}
                                    >
                                        {icon}
                                        {action.title}
                                    </Button>)
                                }) : <></>
                            }
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>
            <Box
                sx={{ width: '100%', height: "auto", bgcolor: 'background.paper' }}
            >
                <FixedSizeList
                    height={listHeight}
                    width={"100%"}
                    itemSize={46}
                    itemCount={listCount}
                    overscanCount={5}
                >
                    {renderRow}
                </FixedSizeList>
            </Box>
            {
                listCount <= 0 ? <Typography sx={{ my: 5, mx: 2 }} color="text.secondary" align="center">
                    {`${vsprintf(t('general.youHaveNoXYet'), [typeNamePlural])}`}
                </Typography> : <></>
            }
        </Paper>
    </>);
};

export default SelectableItemList;